import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Box, Container, CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material';
import HomeScreen from '@src/screens/Home';
import Footer from '@src/components/Footer';
import Terms from '@src/screens/Terms';
import Privacy from '@src/screens/Privacy';
import DataDeletion from '@src/screens/DataDeletion';
import PuenteScreen from '@src/screens/PuenteScreen';
import ConsentScreen from '@src/screens/Consent/ConsentScreen';
import CustomAppBar from '@src/components/CustomAppBar';
import AppDownload from '@src/screens/AppDownload';
import NotFound from '@src/screens/NotFound';
import { buildTheme } from '@src/theme';

const THEME_STORAGE_KEY = 'kabandu-theme';

/** Cada cambio de ruta arranca arriba (react-router conserva el scroll). */
const ScrollToTop: React.FC = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
};

const AppContent: React.FC<{ darkMode: boolean; setDarkMode: (mode: boolean) => void }> = ({
    darkMode,
    setDarkMode
}) => {
    const { pathname, search } = useLocation();
    // Pantalla completa: `/download` solo redirige a la tienda, así que va sin barra,
    // sin pie y sin margen.
    const isFullBleed = pathname === '/download';
    // El expediente dentro de la app (que ya pone su barra «Tu contrato») o en la tablet del
    // personal: sin la barra ni el pie de la web, que ahí solo distraen.
    const params = new URLSearchParams(search);
    const isExpediente = pathname.startsWith('/consent/') && (params.get('app') === '1' || !!params.get('staff'));
    const sinMarco = isFullBleed || isExpediente;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
            <ScrollToTop />
            {!sinMarco && <CustomAppBar darkMode={darkMode} setDarkMode={setDarkMode} />}

            <Container
                component="main"
                maxWidth="md"
                sx={
                    isFullBleed
                        ? { p: 0, maxWidth: '100% !important' }
                        : // Menos aire lateral en móvil: el `py: 4` fijo desperdiciaba
                          // una franja de pantalla en un teléfono.
                          { flex: 1, px: { xs: 1.5, sm: 3 }, py: { xs: 2.5, sm: 4 } }
                }
            >
                <Routes>
                    <Route path="/" element={<HomeScreen />} />
                    <Route path="/download" element={<AppDownload />} />
                    {/* Página puente: el ticket y la gift card se ven en la app (oct/2026). */}
                    <Route path="/gift-card/:id" element={<PuenteScreen recurso="gift-card" />} />
                    <Route path="/order/:id" element={<PuenteScreen recurso="order" />} />
                    {/* Expediente de Studio: en la app o en la tablet; fuera de ellas, puente. */}
                    <Route path="/consent/:id" element={<ConsentScreen />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/data-deletion" element={<DataDeletion />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Container>

            {!sinMarco && <Footer />}
        </Box>
    );
};

const App: React.FC = () => {
    // El tema arranca con la preferencia del SISTEMA de la clienta y respeta su elección
    // si ya cambió el switch (antes siempre arrancaba en oscuro, que además no es el modo
    // de la marca en el resto del sistema).
    const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
    const [darkMode, setDarkMode] = useState<boolean | null>(() => {
        try {
            const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
            return stored === 'dark' ? true : stored === 'light' ? false : null;
        } catch {
            // Safari en modo privado puede lanzar al leer localStorage.
            return null;
        }
    });

    const isDark = darkMode ?? prefersDark;

    const handleSetDarkMode = (mode: boolean) => {
        setDarkMode(mode);
        try {
            window.localStorage.setItem(THEME_STORAGE_KEY, mode ? 'dark' : 'light');
        } catch {
            /* sin persistencia; la sesión actual sí respeta la elección */
        }
    };

    const theme = useMemo(() => buildTheme(isDark ? 'dark' : 'light'), [isDark]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
                <AppContent darkMode={isDark} setDarkMode={handleSetDarkMode} />
            </Router>
        </ThemeProvider>
    );
};

export default App;
